import React, { useState, useEffect, useCallback } from "react";
import "./styles.scss";
import useListQuery from "hooks/useListQuery";
import DataTable from "components/DataTable";
import { getColumns } from "./_components/column";
import { AnphaIcon } from "assets/icons";
import { Button } from "antd";
import OrderStatsWidgets from "./_components/OrderStatsWidgets";
import OrderService from "services/order";
import ProductService from "services/product";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { ExportFile } from "hooks/FileHandler";
import moment from "moment";
import { useSelector } from "react-redux";
import { useModalDirectLinkContext } from "components/ModalDirectLink";
import ModalProgress from "components/ModalProgress/ModalProgress";
import { ORDER_TYPE } from "constants/order";
import { ORDER_PAYMENT_STATUS } from "constants/order";
import { stations_location } from "constants/order";
import { formatPriceNoDecimal } from "helper/common";
import { useQueryClient } from "react-query";
import useExportExcel from "hooks/useExportExcel";
import addKeyLocalStorage from "helper/localStorage";

const DefaultFilterExport = {
  limit: 100,
};

const FIELDS_EXPORT_IMPORT = [
  { api: "orderId", content: "order.id" },
  { api: "orderName", content: "order.orderName" },
  { api: "totalAmount", content: "order.totalAmount" },
  { api: "firstName", content: "order.firstName" },
  { api: "email", content: "order.email" },
  { api: "phoneNumber", content: "order.phoneNumber" },
  { api: "paymentStatus", content: "order.paymentStatus" },
  { api: "orderStatus", content: "order.orderStatus" },
  { api: "createdAt", content: "order.createdAt" },
];

const HistoryOrder = () => {
  const queryClient = useQueryClient();
  const [isModalProgress, setIsModalProgress] = useState(false);
  const [percent, setPercent] = useState(0);
  const [categories, setCategories] = useState([]);
  const { t: translation } = useTranslation();

  const loadCategories = useCallback(async () => {
    try {
      const response = await ProductService.categoryAdvanceUserGetList({ limit: 100, includeSystem: true });
      const list = Array.isArray(response) ? response : response?.data || [];
      setCategories(list);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Mapping status sang tiếng Việt cho export
  const mapStatusForExport = (item) => {
    const orderStatusMap = stations_location.reduce((acc, status) => {
      acc[status.value] = translation(status.label);
      return acc;
    }, {});
    
    const paymentStatusMap = ORDER_PAYMENT_STATUS.reduce((acc, status) => {
      acc[status.value] = translation(status.label);
      return acc;
    }, {});
    
    const roundedTotalAmount = item.totalAmount != null ? formatPriceNoDecimal(item.totalAmount) : item.totalAmount;
    return {
      ...item,
      totalAmount: roundedTotalAmount,
      orderStatus: orderStatusMap[item.orderStatus] || item.orderStatus,
      paymentStatus: paymentStatusMap[item.paymentStatus] || item.paymentStatus,
    };
  };

  // Wrapper fetch với mapping status
  const fetchOrdersForExport = async (params) => {
    const res = await OrderService.getList(params);
    return {
      ...res,
      data: res.data?.map(mapStatusForExport) || [],
    };
  };

  const {
    data,
    loading,
    pagination,
    filters,
    sorter,
    handleTableChange,
    hasMore,
    refetch,
    handleSearch,
    startDate,
    endDate,
    searchValue,
  } = useListQuery(OrderService.getList, "list-order", {
    defaultFilters: {
      orderCategory: 1,
    },
    defaultSorter: {
      key: "createdAt",
      value: "desc",
    },
    defaultStartDate: new Date(),
    defaultEndDate: new Date(),
  });

  const handleRefetch = () => {
    refetch();
    queryClient.invalidateQueries("order-stats");
  };

  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state) => state.member);
  const { setUrl } = useModalDirectLinkContext();

  const { onExportExcel } = ExportFile();

  const { handleExportExcel } = useExportExcel({
    fetchListFn: fetchOrdersForExport,
    fields: FIELDS_EXPORT_IMPORT,
    fileName: "orders",
    defaultFilter: DefaultFilterExport,
    setIsModalProgress,
    setPercent,
    onExportExcel,
    setUrlForModalDirectLink: setUrl,
    translation,
    user,
  });

  const columns = getColumns(translation, history, handleRefetch, categories);

  const extraButtons = [
    <Button
      key="export"
      type="default"
      onClick={() =>
        handleExportExcel({
          dataLength: data.length,
          filters,
          sorter,
          searchValue,
          startDate,
          endDate,
        })
      }
      className="d-flex align-items-center gap-1"
      icon={<AnphaIcon />}
    >
      {translation("extraButtons.export")}
    </Button>,
  ];

  return (
    <>
      <div className="order-table-without-sort">
        {/* <OrderStatsWidgets
          totalOrders={1200}
          totalRevenue={50000000}
          completedOrders={1000}
          processingOrders={200}
        /> */}
        <DataTable
          columns={columns}
          data={data}
          loading={loading}
          pagination={pagination}
          filters={filters}
          sorter={sorter}
          handleTableChange={handleTableChange}
          hasMore={hasMore}
          showFilter={false}
          refetch={handleRefetch}
          extraButtons={extraButtons}
          onSelectedRows={() => {}}
          onInsert={() => {
            history.replace(`${location.pathname}?tab=1`);
          }}
          isFilterByDates
          onSearch={(value) => handleSearch(value)}
          startDate={startDate}
          endDate={endDate}
          columnStorageKey={addKeyLocalStorage(`history-order-columns-${user?.stationsId || "ALL"}`)}
          showSelection={false}
        />
      </div>
      <ModalProgress
        visible={isModalProgress}
        setVisible={setIsModalProgress}
        percent={percent}
      />
    </>
  );
};

export default HistoryOrder;
